import React, { useMemo, useState } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import Conditions from "./conditions";
import { v4 as uuid } from "uuid";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import type { Condition } from "@/workflow/types/Switch";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { NodeData, RefName } from "@/workflow_api/types/workflow";
import Variables from "@/workflow_api/components/variables";
import {
  parseType,
  useGetVariables,
} from "@/workflow_api/hooks/variables/useGetVariables";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { serializeSwitchNode } from "./serialization";
import { Operations } from "../if/operations";
import { Icon } from "@elaniintc/goes-design-system";
import Empty from "@/workflow_api/components/variables/empty";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";
import type { Variable } from "../if/types";

interface Props {
  initialTitle?: string;
}

export const SwitchDetails: React.FC<Props> = ({
  initialTitle = "Operador Switch",
}) => {
  const {
    selectedNodeId,
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
  } = useUIStore();
  const { getNodeById, flowId } = useWorkflowStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;
  const [title, setTitle] = useState<string>(
    nodeData.displayName || initialTitle,
  );
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  const { variables: nodeVariables, isLoading } = useGetVariables();

  const [conditions, setConditions] = useState<Condition[]>(() => {
    const hasInputs =
      nodeData.inputs && Object.keys(nodeData.inputs).length > 0;
    if (!hasInputs)
      return [
        {
          id: `condition_1`,
          label: "Salida 1",
          variable: null,
          operation: null,
          comparativeValue: null,
          comparativeValueType: "input",
        },
      ];

    // Flatten variables for easier lookup
    const allVariables = nodeVariables.flatMap((v) => {
      return (v.variables ?? []).map((variable) => ({
        ...variable,
        node_id: v.nodeId,
        ref_name: v.ref_name || v.type,
      }));
    });

    return Object.values(nodeData.inputs || {}).map((c, index) => {
      const condition = c.conditions?.[0];
      const conditionType = condition?.type;
      const inputType = condition?.input?.type;
      const hasVariable = condition?.input?.source_connections?.length !== 0;
      const optionalInput = condition?.optional_input;
      const optionalInputValue = optionalInput?.value;
      const optionalInputHasConnections =
        (optionalInput?.source_connections?.length || 0) > 0;

      let id = "";
      if (
        condition?.input?.source_connections?.[0]?.field_id &&
        condition?.input?.source_connections?.[0]?.output_name
      ) {
        id = `${condition?.input?.source_connections?.[0].field_id}-${condition?.input?.source_connections?.[0].output_name}`;
      }

      // Extract comparative value - check if it's a variable or a direct value
      let comparativeValue: string | Variable | null = null;
      let comparativeValueType: "input" | "variable" = "input";

      if (
        optionalInputHasConnections &&
        optionalInput?.source_connections?.[0]
      ) {
        const sourceConn = optionalInput.source_connections[0];

        // Try to find the variable in the available variables
        if (sourceConn.field_id && sourceConn.output_name) {
          const foundVar = allVariables.find(
            (v) => v.id === `${sourceConn.field_id}-${sourceConn.output_name}`,
          );
          if (foundVar) {
            comparativeValue = {
              id: foundVar.id,
              name: foundVar.name,
              type: parseType(foundVar.type) || "",
              node_id: foundVar.node_id || sourceConn.source_node_id,
              ref_name: foundVar.ref_name || "",
              ...(foundVar.field_id && { field_id: foundVar.field_id }),
              ...(foundVar.output_name && {
                output_name: foundVar.output_name,
              }),
            };
            comparativeValueType = "variable";
          } else {
            comparativeValueType = "variable";
            comparativeValue = {
              id: `${sourceConn.field_id}-${sourceConn.output_name}`,
              name: sourceConn.field?.name || "",
              type: parseType(sourceConn.field?.type || "") || "",
              node_id: sourceConn.source_node_id,
              ref_name: "",
              field_id: sourceConn.field_id,
              output_name: sourceConn.output_name,
            };
          }
        } else if (sourceConn.field) {
          const foundVar = allVariables.find(
            (v) =>
              v.id === sourceConn.field?.id &&
              v.node_id === sourceConn.source_node_id,
          );
          if (foundVar) {
            comparativeValue = {
              id: foundVar.id,
              name: foundVar.name,
              type: parseType(foundVar.type) || "",
              node_id: foundVar.node_id || sourceConn.source_node_id,
              ref_name: foundVar.ref_name || "",
              ...(foundVar.field_id && { field_id: foundVar.field_id }),
              ...(foundVar.output_name && {
                output_name: foundVar.output_name,
              }),
            };
            comparativeValueType = "variable";
          } else if (sourceConn.field) {
            // Create variable from source_connection if not found in allVariables
            comparativeValue = {
              id: sourceConn.field.id,
              name: sourceConn.field.name || "",
              type: parseType(sourceConn.field.type) || "",
              node_id: sourceConn.source_node_id,
              ref_name: "",
            };
            comparativeValueType = "variable";
          }
        } else if (optionalInput.global_type) {
          const idSuffix =
            optionalInput.global_type === "NODE_EXECUTION_START"
              ? "execution_start"
              : "execution_finish";
          comparativeValueType = "variable";
          comparativeValue = {
            id: `${optionalInput.value}_${idSuffix}`,
            name:
              optionalInput.global_type === "NODE_EXECUTION_START"
                ? "inicio-nodo"
                : "fin-nodo",
            type: "date",
            node_id: optionalInput.value as string,
            ref_name: "",
            global_type: optionalInput.global_type,
          };
        } else if (sourceConn.source_node_id) {
          // Check for node-level variables (like switch node outputs)
          const foundVar = allVariables.find(
            (v) =>
              v.node_id === sourceConn.source_node_id &&
              v.id === sourceConn.source_node_id,
          );
          if (foundVar) {
            comparativeValue = {
              id: foundVar.id,
              name: foundVar.name,
              type: parseType(foundVar.type) || "",
              node_id: foundVar.node_id,
              ref_name: foundVar.ref_name || "",
            };
            comparativeValueType = "variable";
          } else {
            comparativeValueType = "variable";
            if (sourceConn.output_name) {
              const node = getNodeById(
                condition?.input?.source_connections?.[0].source_node_id || "",
              );
              const nodeType: string = (node?.data?.type as string) || "";
              comparativeValue = {
                id: `${sourceConn.source_node_id}-${sourceConn.output_name}`,
                name: sourceConn.output_name || "",
                type: "",
                node_id: sourceConn.source_node_id,
                ref_name: nodeType as string,
                output_name: sourceConn.output_name,
              };
            } else {
              comparativeValue = {
                id: sourceConn.source_node_id,
                name: "",
                type: "",
                node_id: sourceConn.source_node_id,
                ref_name: "",
              };
            }
          }
        }
      } else if (optionalInput?.global_type) {
        if (
          optionalInput?.global_type === "APPLICANT" ||
          optionalInput?.global_type === "CURRENT_TIME"
        ) {
          comparativeValueType = "variable";
          comparativeValue = {
            id: `${optionalInput.value}`,
            name: "",
            type: parseType(optionalInput.type.toLowerCase()) || "",
            node_id: "global-variables",
            ref_name: "",
            global_type: optionalInput.global_type,
          };
        } else {
          const idSuffix =
            optionalInput.global_type === "NODE_EXECUTION_START"
              ? "execution_start"
              : "execution_finish";
          comparativeValueType = "variable";
          comparativeValue = {
            id: `${optionalInput.value}_${idSuffix}`,
            name:
              optionalInput.global_type === "NODE_EXECUTION_START"
                ? "inicio-nodo"
                : "fin-nodo",
            type: "date",
            node_id: optionalInput.value as string,
            ref_name: "",
            global_type: optionalInput.global_type,
          };
        }
      } else if (
        optionalInputValue !== null &&
        optionalInputValue !== undefined
      ) {
        comparativeValue = optionalInputValue.toString();
        comparativeValueType = "input";
      }

      if (
        condition?.input?.global_type === "APPLICANT" ||
        condition?.input?.global_type === "CURRENT_TIME"
      ) {
        return {
          id: `condition_${index + 1}`,
          label: "",
          variable: {
            id: condition?.input?.value as string,
            name: "",
            node_id: "global-variables",
            type: parseType(condition?.input?.type.toLowerCase()) || "",
            ref_name: "",
          },
          operation: condition
            ? Operations[inputType?.toLowerCase() || ""].find((op) => {
                return op.name === conditionType;
              }) || null
            : null,
          comparativeValue: comparativeValue,
          comparativeValueType,
        };
      } else if (condition?.input?.global_type) {
        const idSuffix =
          condition?.input.global_type === "NODE_EXECUTION_START"
            ? "execution_start"
            : "execution_finish";
        return {
          id: `condition_${index + 1}`,
          label: "",
          variable: {
            id: `${condition?.input.value}_${idSuffix}` as string,
            name:
              condition?.input.global_type === "NODE_EXECUTION_START"
                ? "inicio-nodo"
                : "fin-nodo",
            type: "date",
            node_id: condition?.input.value as string,
            ref_name: "",
            global_type: condition?.input.global_type,
          },
          operation: condition
            ? Operations[inputType?.toLowerCase() || ""].find((op) => {
                return op.name === conditionType;
              }) || null
            : null,
          comparativeValue: comparativeValue,
          comparativeValueType,
        };
      }

      if (
        condition?.input?.source_connections &&
        condition?.input?.source_connections?.length > 0 &&
        !condition?.input?.source_connections?.[0]?.field_id &&
        condition?.input?.source_connections?.[0]?.output_name
      ) {
        const node = getNodeById(
          condition?.input?.source_connections?.[0].source_node_id || "",
        );
        const nodeType: string = (node?.data?.type as string) || "";

        return {
          id: `condition_${index + 1}`,
          label:
            nodeData.connections?.[`condition_${index + 1}`].display_name || "",
          variable: {
            id: `${condition?.input?.source_connections?.[0].source_node_id}-${condition?.input?.source_connections?.[0].output_name || ""}`,
            node_id:
              condition?.input?.source_connections?.[0].source_node_id || "",
            output_name:
              condition?.input?.source_connections?.[0].output_name || "",
            name: condition?.input?.source_connections?.[0].output_name || "",
            type:
              parseType(
                condition?.input?.source_connections?.[0].field?.type === "file"
                  ? condition?.input?.source_connections?.[0].field?.config
                      ?.multiple
                    ? "array"
                    : "string"
                  : parseType(
                      condition?.input?.source_connections?.[0].field?.type ||
                        inputType?.toLowerCase() ||
                        "",
                    ),
              ) || "",
            ref_name: nodeType || "",
          },
          operation: condition
            ? Operations[inputType?.toLowerCase() || ""].find((op) => {
                return op.name === conditionType;
              }) || null
            : null,
          comparativeValue,
          comparativeValueType,
        };
      }

      return {
        id: `condition_${index + 1}`,
        label:
          nodeData.connections?.[`condition_${index + 1}`].display_name || "",
        variable: hasVariable
          ? {
              node_id:
                condition?.input?.source_connections?.[0].source_node_id || "",
              id:
                id ||
                condition?.input?.source_connections?.[0]?.field?.id ||
                condition?.input?.source_connections?.[0].source_node_id ||
                "",
              name:
                condition?.input?.source_connections?.[0]?.field?.name || "",
              type:
                parseType(
                  condition?.input?.source_connections?.[0].field?.type ===
                    "file"
                    ? condition?.input?.source_connections?.[0].field?.config
                        ?.multiple
                      ? "array"
                      : "string"
                    : parseType(
                        condition?.input?.source_connections?.[0].field?.type ||
                          inputType?.toLowerCase() ||
                          "",
                      ),
                ) || "",
              ref_name: "",
              ...(condition?.input?.source_connections?.[0].output_name
                ? {
                    output_name:
                      condition?.input?.source_connections?.[0].output_name,
                  }
                : {}),
              ...(condition?.input?.source_connections?.[0].field_id
                ? {
                    field_id:
                      condition?.input?.source_connections?.[0].field_id,
                  }
                : {}),
            }
          : null,
        operation: condition
          ? Operations[inputType?.toLowerCase() || ""].find((op) => {
              return op.name === conditionType;
            }) || null
          : null,
        comparativeValue,
        comparativeValueType,
      };
    });
  });

  const onAddCondition = () => {
    setConditions((prev) => [
      ...prev,
      {
        id: uuid(),
        variable: null,
        operation: null,
        comparativeValue: null,
        comparativeValueType: "input",
        label: "",
      },
    ]);
  };

  const onDeleteCondition = (id: string) => {
    setConditions((prev) => prev.filter((c) => c.id !== id));
  };

  const onUpdateCondition = (id: string, condition: Condition) => {
    setConditions((prev) => prev.map((c) => (c.id === id ? condition : c)));
  };

  const { mutateAsync } = useUpdateNode(flowId);

  const onSaveNode = async () => {
    if (!node) return;
    const nodeData = node.data as unknown as NodeData;

    // Serializar nodo
    const serializedInputs = serializeSwitchNode(conditions);

    mutateAsync({
      node_id: node.id,
      node: {
        flow_id: flowId,
        ref_name: nodeData.type as RefName,
        display_name: title,
        order: nodeData.order,
        position_x: node.position.x,
        position_y: node.position.y,
        type: nodeData.node_type,
        manual_continue: manualConfirmation,
        inputs: serializedInputs,
        connections: conditions.reduce(
          (acc, condition, index) => {
            acc[`condition_${index + 1}`] = {
              display_name: condition.label || `Salida ${index + 1}`,
              target_node: nodeData.connections
                ? nodeData.connections[`condition_${index + 1}`]?.target_node ||
                  null
                : null,
            };
            return acc;
          },
          {} as Record<
            string,
            { display_name: string; target_node: string | null }
          >,
        ),
      },
    });

    setNodeDetailsOpen(false);
    setSelectedNodeId(undefined);
  };

  const areConditionsValid = useMemo(() => {
    return conditions.every(
      (c) =>
        c.variable &&
        c.operation &&
        (!c.operation.needsComparativeValue ||
          (c.comparativeValue !== null &&
            c.comparativeValue !== undefined &&
            c.comparativeValue !== "")),
    );
  }, [conditions]);

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        onClose={() => {
          setModalState("close-warning");
        }}
        isSaveValid={areConditionsValid && isNameValid(title)}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          onSaveNode();
        }}
      >
        <ThreeColumnLayout
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
          }}
          centerHeader={{
            icon: "arrows-cross",
            title: title,
            color: "neutral",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
              <Variables sourceNodeId={selectedNodeId!} />
            </div>
          }
          centerContent={
            <>
              {isLoading && (
                <div className="w-full flex items-center justify-center min-h-[300px]">
                  <Icon
                    name="spinner-third"
                    className="animate-spin text-on-disabled"
                    type="far"
                  />
                </div>
              )}
              {!isLoading && (
                <Conditions
                  conditions={conditions}
                  areConditionsValid={areConditionsValid}
                  onAddCondition={onAddCondition}
                  onDeleteCondition={onDeleteCondition}
                  onUpdateCondition={onUpdateCondition}
                  variables={nodeVariables}
                />
              )}
            </>
          }
          rightContent={
            <>
              <Empty />
            </>
          }
        />
      </FullModal>
    </>
  );
};
